// Confirm dialog host — renders the modal when confirmStore has an
// open request. Mount once in AppLayout (alongside ToastViewport).
//
// To trigger from anywhere:
//   import { confirm } from "@/stores/confirmStore";
//   const ok = await confirm({ title: "...", message: "...", variant: "danger" });
//   if (!ok) return;

import { useEffect } from "react";
import { Button } from "@/components/ui/Button";
import { useConfirmStore } from "@/stores/confirmStore";
import { useDict } from "@/i18n/LocaleProvider";

export function ConfirmHost() {
  const t = useDict().app.ui;
  const open = useConfirmStore((s) => s.open);
  const resolve = useConfirmStore((s) => s.resolve);

  // Esc cancels; Enter confirms. Only attached when a dialog is open.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") resolve(false);
      else if (e.key === "Enter") resolve(true);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, resolve]);

  if (!open) return null;

  const variant = open.variant ?? "default";
  const confirmLabel =
    open.confirmLabel ?? (variant === "danger" ? t.confirmDelete : t.confirm);
  const cancelLabel = open.cancelLabel ?? t.cancel;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.6)" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      onClick={() => resolve(false)}
    >
      <div
        className="w-full max-w-md rounded animate-fade-in"
        style={{
          background: "rgb(var(--color-bg2))",
          border: `1px solid ${variant === "danger" ? "rgb(var(--color-danger))" : "rgb(var(--color-stroke))"}`,
          boxShadow: "0 24px 60px rgba(0,0,0,0.7)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-stroke">
          <div
            id="confirm-title"
            className="font-display text-[16px] font-semibold"
            style={{ color: variant === "danger" ? "rgb(var(--color-danger))" : "rgb(var(--color-content))" }}
          >
            {open.title}
          </div>
        </div>
        <div className="px-4 py-3">
          <div className="font-sans text-[13px] text-content leading-[1.6] whitespace-pre-wrap">
            {open.message}
          </div>
        </div>
        {/* Buttons stack on phones (44px touch targets, no thumb misses
            on adjacent buttons), revert to side-by-side from sm+. The
            confirm button comes first in stack order so the primary
            action is closest to the thumb at the bottom of the dialog. */}
        <div className="px-4 py-3 border-t border-stroke flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button variant="default" onClick={() => resolve(false)}>
            {cancelLabel}
          </Button>
          <Button
            variant={variant === "danger" ? "danger" : "primary"}
            onClick={() => resolve(true)}
            // Autofocus on Confirm so Enter works as expected
            autoFocus
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
